import React, { FC, ReactElement, useMemo } from 'react';
import { Theme, useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import InputLabel from '@mui/material/InputLabel';
import FilledInput from '@mui/material/FilledInput';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import ListItemText from '@mui/material/ListItemText';
import Checkbox from '@mui/material/Checkbox';
import Select, { SelectChangeEvent, BaseSelectProps } from '@mui/material/Select';
import { clsx } from 'clsx';
// Components
import SvgIcon from '../icons/SvgIcon';
// Resources
import { stringsRes } from '../../resources/strings';
import { SelectOption } from '../../core/apiTypes';
import { IconCommon } from '../../resources/images';
import { MAX_FILTER_LABEL } from '../../utils/constants';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

interface SelectMultipleProps extends Omit<BaseSelectProps, 'variant'> {
    label: string;
    startValue?: string; // value seperated by ,
    emptyLabel?: string; // default: Tous
    options?: SelectOption[];
    onSelectChange?: (vals: SelectOption[]) => void;
    onClosePicker?: (vals: SelectOption[]) => void;
    containerClassName?: string;
}

const SelectMultiple: FC<SelectMultipleProps> = (props): ReactElement => {
    const { id, label, startValue, emptyLabel, options, onSelectChange, onClosePicker, containerClassName, ...rest } =
        props;
    const classes = useStyles();
    const theme = useTheme();
    const [personName, setPersonName] = React.useState<string[]>(startValue?.split(',') || []);

    const MenuProps = useMemo(
        () => ({
            PaperProps: {
                style: {
                    maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
                    width: 250,
                    backgroundColor: theme.palette.colors.background.appBar,
                    marginTop: 2,
                },
            },
        }),
        [theme.palette.colors.background.appBar],
    );

    const allOptions = useMemo(() => {
        const testData: SelectOption[] = [
            { id: '1', label: 'Oliver Hansen' },
            { id: '2', label: 'Van Henry' },
            { id: '3', label: 'April Tucker' },
            { id: '4', label: 'Ralph Hubbard' },
            { id: '5', label: 'Omar Alexander' },
        ];

        return [...(options || testData)];
    }, [options]);

    const handleChange = (event: SelectChangeEvent<typeof personName>) => {
        const {
            target: { value },
        } = event;
        // On autofill we get a stringified value.
        const vals = typeof value === 'string' ? value.split(',') : value;
        setPersonName(vals);
        onSelectChange?.(vals.map((v) => allOptions.find((o) => o.id === v)));
    };

    const handleClose = () => {
        onClosePicker?.(personName.map((v) => allOptions.find((o) => o.id === v)));
    };

    return (
        <FormControl
            sx={{ m: 1, width: rest.fullWidth ? '100%' : '50%' }}
            className={clsx(classes.selectMultiple, containerClassName)}
        >
            <InputLabel htmlFor={id || 'select-multiple-checkbox'} className="MuiInputLabel-shrink">
                {label}
            </InputLabel>
            <Select
                id={id || 'select-multiple-checkbox'}
                color="secondary"
                multiple
                value={personName}
                onChange={handleChange}
                onClose={handleClose}
                input={<FilledInput id={id || 'select-multiple-checkbox'} />}
                displayEmpty
                notched={undefined}
                IconComponent={(iconProps) =>
                    !rest.disabled && (
                        <div className={`material-icons ${iconProps.className} ${classes.icon}`}>
                            <SvgIcon icon={IconCommon.ChevronDown} color="black" fill="none" />
                        </div>
                    )
                }
                renderValue={(selected: string[]) => {
                    if (selected.length === allOptions.length) {
                        return stringsRes.common.all;
                    }
                    if (selected.length === 0) {
                        return emptyLabel ?? stringsRes.common.all;
                    }

                    const firstName = selected.map((v) => allOptions.find((o) => o.id === v)?.label)[0];
                    return `${
                        firstName?.length > MAX_FILTER_LABEL ? `${firstName.slice(0, MAX_FILTER_LABEL)}...` : firstName
                    } ${selected.length > 1 ? `+ ${selected.length - 1}` : ''}`;
                }}
                MenuProps={MenuProps}
                {...rest}
            >
                {allOptions.map((option) => (
                    <MenuItem key={`${option.id}—${option.label}`} value={option.id} disabled={option.disabled}>
                        <Checkbox
                            checked={personName.indexOf(option.id) > -1}
                            color="secondary"
                            icon={<SvgIcon icon={IconCommon.Radio} />}
                            checkedIcon={<SvgIcon icon={IconCommon.RadioChecked} />}
                        />
                        <ListItemText
                            className={classes.itemSelect}
                            primary={<span className={classes.truncatedSelectValue}>{option.label}</span>}
                            title={option.label}
                        />
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        icon: {
            width: 20,
            height: 20,
            right: 16,
        },
        itemSelect: {
            '& .MuiListItemText-primary': {
                display: 'inline-flex',
            },
        },
        truncatedSelectValue: {
            width: 250,
            display: 'inline-block',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        selectMultiple: {
            margin: 0,
            '& .MuiFilledInput-root': {
                background: theme.palette.colors.background.primaryElement,
                borderRadius: 6,
                fontWeight: 500,
                fontSize: 16,
                letterSpacing: '0.2px',
                border: `1.5px solid transparent`,
            },
            '& div.Mui-focused': {
                background: theme.palette.colors.background.secondaryBtn,
                border: `1.5px solid ${theme.palette.secondary.main} !important`,
            },
            '& :before': {
                borderBottomColor: 'transparent!important',
            },
            '& .MuiFilledInput-underline:after': {
                borderBottomColor: 'transparent',
            },
            '& label.MuiInputLabel-shrink': {
                marginTop: theme.spacing(1.75),
            },
            '& label.Mui-focused': {
                color: theme.palette.secondary.main,
                fontWeight: 500,
                fontSize: 16,
                letterSpacing: '0.2px',
            },
            '& label': {
                fontWeight: 500,
                fontSize: 16,
                letterSpacing: '0.2px',
            },
            '& label:not(.Mui-focused)': {
                '-webkit-transform': 'translate(14px, -9px) scale(0.75)',
                '-moz-transform': 'translate(14px, -9px) scale(0.75)',
                '-ms-transform': 'translate(14px, -9px) scale(0.75)',
                transform: 'translate(14px, -9px) scale(0.75)',
            },
            '& label.Mui-focused.Mui-error': {
                color: theme.palette.colors.text.error,
                fontWeight: 500,
                fontSize: 16,
                letterSpacing: '0.2px',
            },
            '& label.MuiFormLabel-root.Mui-error': {
                color: theme.palette.colors.text.error,
            },
            '& :focus': {
                borderRadius: 6,
            },
            '& .MuiFormHelperText-root.Mui-error': {
                color: theme.palette.colors.text.error,
            },
            '& div.Mui-error': {
                border: `1.5px solid ${theme.palette.colors.text.error}`,
            },
        },
    }),
);

export default SelectMultiple;
